import { useEffect, useState } from 'react';
import {
  DAY_LABELS,
  DEFAULT_AVAILABLE_TIMES,
  TIME_24H_RE,
  WEEK_DAYS_ORDER,
  defaultSalonSettings,
  formatTime24hInput,
  type SalonSettings,
  type WeekDay
} from '@/lib/salonSettings';
import { fetchTenantBusiness, saveTenantBusiness } from '@/lib/tenantBusiness';

export function AvailabilityEditor() {
  const [settings, setSettings] = useState<SalonSettings>(() => defaultSalonSettings());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const availableTimes = settings.availableTimes ?? DEFAULT_AVAILABLE_TIMES;

  useEffect(() => {
    fetchTenantBusiness('current')
      .then(setSettings)
      .catch(() => setMessage('Не удалось загрузить доступное время.'))
      .finally(() => setLoading(false));
  }, []);

  const setDayTimes = (day: WeekDay, times: string[]) => {
    setSettings((current) => ({
      ...current,
      availableTimes: { ...(current.availableTimes ?? DEFAULT_AVAILABLE_TIMES), [day]: times }
    }));
    setMessage(null);
  };

  const save = async () => {
    const hasInvalidTime = WEEK_DAYS_ORDER.some((day) =>
      (availableTimes[day] ?? []).some((time) => !TIME_24H_RE.test(time))
    );
    if (hasInvalidTime) {
      setMessage('Введите время в 24-часовом формате ЧЧ:ММ, например 09:17.');
      return;
    }
    try {
      setSaving(true);
      await saveTenantBusiness('current', settings);
      setMessage('Доступное время сохранено.');
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : 'Не удалось сохранить время.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="mnc-admin-panel">
      <div className="mnc-admin-panel-head">
        <div><span className="mnc-eyebrow">Онлайн-запись</span><h2>Доступное время</h2></div>
      </div>
      <p className="mnc-muted">Добавьте точное время начала записи. Можно указать любое время с точностью до минуты.</p>
      {loading ? <p className="mnc-muted">Загружаем расписание…</p> : (
        <div className="mnc-availability-editor">
          {WEEK_DAYS_ORDER.map((day) => {
            const dayTimes = availableTimes[day] ?? [];
            return (
              <div className="mnc-availability-day" key={day}>
                <b>{DAY_LABELS[day]}</b>
                <div className="mnc-availability-times">
                  {dayTimes.map((time, index) => (
                    <span className="mnc-availability-time" key={day + '-' + index}>
                      <input
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        placeholder="ЧЧ:ММ"
                        pattern="(?:[01][0-9]|2[0-3]):[0-5][0-9]"
                        maxLength={5}
                        title="Время в 24-часовом формате ЧЧ:ММ"
                        aria-label={DAY_LABELS[day] + ', доступное время ' + (index + 1)}
                        value={time}
                        onChange={(event) => setDayTimes(
                          day,
                          dayTimes.map((value, timeIndex) => timeIndex === index
                            ? formatTime24hInput(event.target.value)
                            : value)
                        )}
                      />
                      <button
                        className="mnc-linklike is-danger"
                        aria-label={'Удалить ' + time + ' в ' + DAY_LABELS[day]}
                        onClick={() => setDayTimes(day, dayTimes.filter((_, timeIndex) => timeIndex !== index))}
                      >Удалить</button>
                    </span>
                  ))}
                  {dayTimes.length === 0 && <span className="mnc-muted">Нет доступного времени</span>}
                </div>
                <button
                  className="mnc-btn mnc-btn-ghost mnc-btn-sm"
                  onClick={() => setDayTimes(day, [...dayTimes, '12:00'])}
                >
                  Добавить время
                </button>
              </div>
            );
          })}
        </div>
      )}
      <div className="mnc-actions">
        <button className="mnc-btn" disabled={loading || saving} onClick={save}>
          {saving ? 'Сохраняем…' : 'Сохранить время'}
        </button>
      </div>
      {message && <p className="mnc-muted">{message}</p>}
    </section>
  );
}
